/** @typedef {import('headlight-configurable-application').Pict} Pict */

const BulmaHTMLInputHandler = require('./PictProvider-Bulma-HTML.js');

/**
 * Renders a scannable QR code image into an input's display container from a QR code value.
 *
 * Subclasses the Bulma-HTML extension so it inherits the DISPLAY-FOR container plumbing (single
 * and tabular) and only swaps the content generation: the value (a Sample.QRCode string) is turned
 * into an <img> via QRCodeProvider.create. That path is fully offline — a canvas drawn by the
 * bundled window.QRCode library, no network round-trip — so the code is scannable on a sample that
 * has not been saved yet.
 *
 * @class
 * @extends BulmaHTMLInputHandler
 * @memberof providers.inputs
 */
class MaterialsQRCodeDisplayInputHandler extends BulmaHTMLInputHandler
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Build the <img> HTML for a QR code value, or null when there is nothing to render (the base
	 * class skips assignment on a falsy return, leaving the template's "no QR code" message).
	 *
	 * @param {Object} pInput - The input descriptor (reads PictForm.QRCodeSize, default 192px).
	 * @param {any} pValue - The QR code value (a Sample.QRCode string).
	 * @return {string|null}
	 */
	getInputContent(pInput, pValue)
	{
		if (!pValue || typeof pValue !== 'string')
		{
			return null;
		}
		const tmpQRCodeProvider = this.pict.providers.QRCodeProvider;
		if (!tmpQRCodeProvider || typeof tmpQRCodeProvider.create !== 'function')
		{
			return null;
		}
		const tmpSize = (pInput && pInput.PictForm && pInput.PictForm.QRCodeSize) || 192;
		try
		{
			const tmpSource = tmpQRCodeProvider.create(pValue, { size: tmpSize });
			const tmpImage = `<img src="${tmpSource}" alt="Sample QR Code" style="width:${tmpSize}px;height:${tmpSize}px;" />`;
			const tmpPrintButton = this._getPrintButtonHTML(pValue);
			// A block is not moved by the container's text centering, so the button stays under the code.
			return tmpPrintButton ? `<div style="width:${tmpSize}px;text-align:center;">${tmpImage}${tmpPrintButton}</div>` : tmpImage;
		}
		catch (pError)
		{
			this.pict.log.warn(`MaterialsQRCodeDisplay could not render a QR code: ${pError.message || pError}`);
			return null;
		}
	}

	/**
	 * The Print label button shown under the code, for interactive forms in an app that can print.
	 *
	 * @param {string} pValue - The QR code value (a Sample.QRCode string).
	 * @return {string}
	 */
	_getPrintButtonHTML(pValue)
	{
		const tmpSampleProvider = this.pict.providers.SampleProvider;
		if (!this.pict.AppData?.Interactive || !tmpSampleProvider || typeof tmpSampleProvider.printFormSample !== 'function')
		{
			return '';
		}
		const tmpEscapedValue = pValue.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		const tmpOnClick = '_Pict.providers.SampleProvider.printFormSample(this.dataset.qrcode)';
		const tmpLabel = '<span class="icon"><i class="fas fa-fw fa-print"></i></span><span>Print label</span>';
		return `<div class="mt-2 is-hidden-print"><button type="button" class="button is-primary is-small" data-qrcode="${tmpEscapedValue}" onclick="${tmpOnClick}">${tmpLabel}</button></div>`;
	}

	/**
	 * Toggle the template's EMPTY-MESSAGE vs DISPLAY container after (re)assigning content. The
	 * `{~ShowContent~}`/`{~HideContent~}` classes are baked at render time from the value as it was
	 * THEN; the Sample QRCode is synthesized AFTER first render, so without this the field stays on
	 * "No QR code available" even once the value (and the <img>) have arrived in the DISPLAY div.
	 *
	 * @param {Object} pInput
	 * @param {boolean} pHasContent
	 * @return {void}
	 */
	_setContentVisibility(pInput, pHasContent)
	{
		const tmpRawHTMLID = pInput && pInput.Macro && pInput.Macro.RawHTMLID;
		if (!tmpRawHTMLID || typeof document === 'undefined')
		{
			return;
		}
		const tmpDisplay = document.getElementById(`DISPLAY-FOR-${tmpRawHTMLID}`);
		const tmpEmpty = document.getElementById(`EMPTY-MESSAGE-FOR-${tmpRawHTMLID}`);
		if (tmpDisplay)
		{
			tmpDisplay.classList.toggle('is-hidden', !pHasContent);
		}
		if (tmpEmpty)
		{
			tmpEmpty.classList.toggle('is-hidden', !!pHasContent);
		}
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pGroup
	 * @param {Object} pRow
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {string} pTransactionGUID
	 * @return {boolean}
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._setContentVisibility(pInput, !!(pValue && String(pValue).trim()));
		return tmpResult;
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pGroup
	 * @param {Object} pRow
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {string} pTransactionGUID
	 * @return {boolean}
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const tmpResult = super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		this._setContentVisibility(pInput, !!(pValue && String(pValue).trim()));
		return tmpResult;
	}
}

module.exports = MaterialsQRCodeDisplayInputHandler;
